import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { 
  Calendar, 
  Clock, 
  Info, 
  AlertTriangle,
  CheckCircle2
} from 'lucide-react';
import type { WorkingPattern } from '@/types/timesheet';
import { cn } from '@/lib/utils';

interface WorkingPatternFormProps {
  workingPattern?: WorkingPattern;
  onChange?: (pattern: WorkingPattern, isValid: boolean) => void;
  onSave?: (pattern: WorkingPattern) => void;
  onCancel?: () => void;
  readOnly?: boolean;
  showTitle?: boolean;
  variant?: 'default' | 'compact';
  className?: string;
  hideConfigurationAlert?: boolean;
}

const DEFAULT_PATTERN: WorkingPattern = {
  hoursPerWeek: 40,
  workingDays: {
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: false,
    sunday: false,
  },
  dailyHours: {
    monday: 8,
    tuesday: 8,
    wednesday: 8,
    thursday: 8,
    friday: 8,
    saturday: 0,
    sunday: 0,
  },
};

// Quick preset patterns
const PRESET_PATTERNS = [
  { label: 'Part-time (20h)', hours: 20, days: ['monday', 'tuesday', 'wednesday'], description: '3 days/week' },
  { label: 'Part-time (30h)', hours: 30, days: ['monday', 'tuesday', 'wednesday', 'thursday'], description: '4 days/week' },
  { label: 'Full-time (37.5h)', hours: 37.5, days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'], description: '5 days/week' },
  { label: 'Full-time (40h)', hours: 40, days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'], description: '5 days/week' },
];

export const WorkingPatternForm: React.FC<WorkingPatternFormProps> = ({
  workingPattern,
  onChange,
  onSave,
  onCancel,
  readOnly = false,
  showTitle = true,
  variant = 'default',
  className,
  hideConfigurationAlert = false,
}) => {
  const [pattern, setPattern] = useState<WorkingPattern>(() => {
    const initial = workingPattern || DEFAULT_PATTERN;
    // Initialize dailyHours if not present
    if (!initial.dailyHours) {
      const workingDaysCount = Object.values(initial.workingDays).filter(Boolean).length;
      const hoursPerDay = workingDaysCount > 0 ? initial.hoursPerWeek / workingDaysCount : 0;
      initial.dailyHours = {
        monday: initial.workingDays.monday ? hoursPerDay : 0,
        tuesday: initial.workingDays.tuesday ? hoursPerDay : 0,
        wednesday: initial.workingDays.wednesday ? hoursPerDay : 0,
        thursday: initial.workingDays.thursday ? hoursPerDay : 0,
        friday: initial.workingDays.friday ? hoursPerDay : 0,
        saturday: initial.workingDays.saturday ? hoursPerDay : 0,
        sunday: initial.workingDays.sunday ? hoursPerDay : 0,
      };
    }
    return initial;
  });

  // Notify parent of changes
  useEffect(() => {
    if (onChange) {
      const workingDaysCount = Object.values(pattern.workingDays).filter(Boolean).length;
      const isValid = workingDaysCount > 0 && pattern.hoursPerWeek > 0 && pattern.hoursPerWeek <= 168;
      onChange(pattern, isValid);
    }
  }, [pattern, onChange]);

  // Update local state when prop changes (for reset functionality)
  useEffect(() => {
    if (workingPattern) {
      const updated = { ...workingPattern };
      // Initialize dailyHours if not present
      if (!updated.dailyHours) {
        const workingDaysCount = Object.values(updated.workingDays).filter(Boolean).length;
        const hoursPerDay = workingDaysCount > 0 ? updated.hoursPerWeek / workingDaysCount : 0;
        updated.dailyHours = {
          monday: updated.workingDays.monday ? hoursPerDay : 0,
          tuesday: updated.workingDays.tuesday ? hoursPerDay : 0,
          wednesday: updated.workingDays.wednesday ? hoursPerDay : 0,
          thursday: updated.workingDays.thursday ? hoursPerDay : 0,
          friday: updated.workingDays.friday ? hoursPerDay : 0,
          saturday: updated.workingDays.saturday ? hoursPerDay : 0,
          sunday: updated.workingDays.sunday ? hoursPerDay : 0,
        };
      }
      setPattern(updated);
    }
  }, [workingPattern]);

  const handleHoursChange = (value: string) => {
    const hours = parseFloat(value) || 0;
    setPattern({ ...pattern, hoursPerWeek: hours });
  };

  const handleDayToggle = (day: keyof WorkingPattern['workingDays']) => {
    const isCurrentlyEnabled = pattern.workingDays[day];
    const newWorkingDays = {
      ...pattern.workingDays,
      [day]: !isCurrentlyEnabled,
    };
    
    // Update daily hours when toggling a day
    const newDailyHours = { ...pattern.dailyHours! };
    if (!isCurrentlyEnabled) {
      // Enabling a day - calculate default hours
      const currentWorkingDaysCount = Object.values(pattern.workingDays).filter(Boolean).length;
      const defaultHours = currentWorkingDaysCount > 0 ? pattern.hoursPerWeek / (currentWorkingDaysCount + 1) : pattern.hoursPerWeek;
      newDailyHours[day] = defaultHours;
    } else {
      // Disabling a day - set to 0
      newDailyHours[day] = 0;
    }
    
    // Recalculate total weekly hours
    const newTotalHours = Object.values(newDailyHours).reduce((sum, h) => sum + h, 0);
    
    setPattern({
      ...pattern,
      workingDays: newWorkingDays,
      dailyHours: newDailyHours,
      hoursPerWeek: newTotalHours,
    });
  };

  const handleDailyHoursChange = (day: keyof WorkingPattern['workingDays'], value: string) => {
    const hours = parseFloat(value) || 0;
    
    // Validate max 24 hours per day
    if (hours > 24) return;
    
    const newDailyHours = {
      ...pattern.dailyHours!,
      [day]: hours,
    };
    
    // Recalculate total weekly hours
    const newTotalHours = Object.values(newDailyHours).reduce((sum, h) => sum + h, 0);
    
    setPattern({
      ...pattern,
      dailyHours: newDailyHours,
      hoursPerWeek: newTotalHours,
    });
  };

  const handleTotalWeeklyHoursChange = (value: string) => {
    const newTotal = parseFloat(value) || 0;
    
    // Validate range
    if (newTotal < 0 || newTotal > 168) return;

    // Get list of working days
    const workingDays = Object.entries(pattern.workingDays)
      .filter(([_, isWorking]) => isWorking)
      .map(([day]) => day as keyof WorkingPattern['workingDays']);

    if (workingDays.length === 0) {
      // No working days, just update the total
      setPattern({
        ...pattern,
        hoursPerWeek: newTotal,
      });
      return;
    }

    // Distribute hours evenly across working days
    const hoursPerDay = newTotal / workingDays.length;
    const roundedHoursPerDay = Math.round(hoursPerDay * 4) / 4; // Round to nearest 0.25

    const newDailyHours = { ...pattern.dailyHours! };
    
    // Set all working days to the calculated hours
    workingDays.forEach(day => {
      newDailyHours[day] = roundedHoursPerDay;
    });

    // Adjust for rounding differences to match exact total
    const actualTotal = workingDays.reduce((sum, day) => sum + newDailyHours[day], 0);
    if (actualTotal !== newTotal && workingDays.length > 0) {
      const difference = newTotal - actualTotal;
      // Add the difference to the first working day
      newDailyHours[workingDays[0]] = Math.round((newDailyHours[workingDays[0]] + difference) * 4) / 4;
      
      // Ensure no day exceeds 24 hours
      if (newDailyHours[workingDays[0]] > 24) {
        newDailyHours[workingDays[0]] = 24;
      }
    }

    setPattern({
      ...pattern,
      dailyHours: newDailyHours,
      hoursPerWeek: newTotal,
    });
  };

  const handleNotesChange = (value: string) => {
    setPattern({ ...pattern, notes: value });
  };

  // Apply a preset pattern (sets both hours and days)
  const applyPreset = (hours: number, days: string[]) => {
    const hoursPerDay = hours / days.length;
    const newWorkingDays = {
      monday: days.includes('monday'),
      tuesday: days.includes('tuesday'),
      wednesday: days.includes('wednesday'),
      thursday: days.includes('thursday'),
      friday: days.includes('friday'),
      saturday: days.includes('saturday'),
      sunday: days.includes('sunday'),
    };
    
    const newDailyHours = {
      monday: days.includes('monday') ? hoursPerDay : 0,
      tuesday: days.includes('tuesday') ? hoursPerDay : 0,
      wednesday: days.includes('wednesday') ? hoursPerDay : 0,
      thursday: days.includes('thursday') ? hoursPerDay : 0,
      friday: days.includes('friday') ? hoursPerDay : 0,
      saturday: days.includes('saturday') ? hoursPerDay : 0,
      sunday: days.includes('sunday') ? hoursPerDay : 0,
    };
    
    setPattern({
      ...pattern,
      hoursPerWeek: hours,
      workingDays: newWorkingDays,
      dailyHours: newDailyHours,
    });
  };

  const workingDaysCount = Object.values(pattern.workingDays).filter(Boolean).length;
  const hoursPerDay = workingDaysCount > 0 ? pattern.hoursPerWeek / workingDaysCount : 0;

  const days: { key: keyof WorkingPattern['workingDays']; label: string; short: string }[] = [
    { key: 'monday', label: 'Monday', short: 'Mon' },
    { key: 'tuesday', label: 'Tuesday', short: 'Tue' },
    { key: 'wednesday', label: 'Wednesday', short: 'Wed' },
    { key: 'thursday', label: 'Thursday', short: 'Thu' },
    { key: 'friday', label: 'Friday', short: 'Fri' },
    { key: 'saturday', label: 'Saturday', short: 'Sat' },
    { key: 'sunday', label: 'Sunday', short: 'Sun' },
  ];

  const hasValidationErrors = workingDaysCount === 0 || pattern.hoursPerWeek <= 0 || pattern.hoursPerWeek > 168;

  if (variant === 'compact') {
    return (
      <div className={cn('space-y-4', className)}>
        {showTitle && (
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <h4 className="text-sm font-medium">Working Pattern</h4>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="hours-compact">Hours per Week</Label>
            <Input
              id="hours-compact"
              type="number"
              min="0"
              max="168"
              step="0.5"
              value={pattern.hoursPerWeek}
              onChange={(e) => handleHoursChange(e.target.value)}
              disabled={readOnly}
              className="font-mono"
            />
          </div>

          <div className="space-y-2">
            <Label>Working Days</Label>
            <div className="flex flex-wrap gap-2">
              {days.map((day) => (
                <Button
                  key={day.key}
                  type="button"
                  variant={pattern.workingDays[day.key] ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => !readOnly && handleDayToggle(day.key)}
                  disabled={readOnly}
                  className={cn(
                    'h-8 px-3',
                    !readOnly && 'cursor-pointer',
                    readOnly && 'cursor-default opacity-70'
                  )}
                >
                  {day.short}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {workingDaysCount > 0 && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              <span className="font-medium">{hoursPerDay.toFixed(1)} hours per day</span> across{' '}
              {workingDaysCount} working {workingDaysCount === 1 ? 'day' : 'days'}
            </AlertDescription>
          </Alert>
        )}

        {/* Legacy support: Keep individual save button if onSave is provided directly */}
        {!readOnly && onSave && (
          <div className="flex gap-2">
            <Button onClick={() => onSave(pattern)} disabled={hasValidationErrors} size="sm">
              Save Changes
            </Button>
            {onCancel && (
              <Button onClick={onCancel} variant="outline" size="sm">
                Cancel
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-muted-foreground" />
          <CardTitle>Working Pattern</CardTitle>
        </div>
        <CardDescription>
          {readOnly
            ? 'View working hours and schedule'
            : 'Configure working hours and schedule for this team member'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Consolidated Alert Header */}
        {!readOnly && !hideConfigurationAlert && (
          <Alert className="bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
            <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <AlertDescription className="text-sm text-blue-900 dark:text-blue-100">
              <div className="space-y-1">
                <p className="font-medium">Working Pattern Configuration</p>
                <ul className="text-xs space-y-0.5 mt-2">
                  <li>• Used to calculate utilization targets and validate timesheet entries</li>
                  <li>• Changes will affect future time tracking calculations</li>
                  <li>• Use quick presets below for common working patterns</li>
                </ul>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Total Hours per Week & Quick Presets - Inline like General Settings */}
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-4">
            {/* Total Hours per Week - Left Side */}
            <div className="flex items-center gap-3">
              <Label htmlFor="hours-per-week" className="text-sm font-medium whitespace-nowrap">
                Total Hours per Week
              </Label>
              <Input
                id="hours-per-week"
                type="number"
                value={pattern.hoursPerWeek.toFixed(2)}
                onChange={(e) => !readOnly && handleTotalWeeklyHoursChange(e.target.value)}
                disabled={readOnly}
                className="w-24 font-semibold"
                min="0"
                max="168"
                step="0.5"
              />
            </div>

            {/* Quick Presets - Right Side */}
            {!readOnly && (
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium whitespace-nowrap">Quick Presets:</Label>
                {PRESET_PATTERNS.map((preset) => (
                  <Button
                    key={preset.label}
                    variant="outline"
                    size="sm"
                    onClick={() => applyPreset(preset.hours, preset.days)}
                    className="h-8 px-3 text-xs"
                  >
                    {preset.hours}h
                  </Button>
                ))}
              </div>
            )}
          </div>

          {/* Help Text */}
          {!readOnly && (
            <p className="text-xs text-muted-foreground">
              Edit total hours to distribute evenly across working days, or use quick presets for common patterns
            </p>
          )}

          {/* Validation Alerts */}
          {pattern.hoursPerWeek > 168 && (
            <Alert variant="destructive" className="py-2">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="text-xs">
                Total exceeds 168 hours per week. Please adjust daily hours.
              </AlertDescription>
            </Alert>
          )}
          {pattern.hoursPerWeek <= 0 && !readOnly && (
            <Alert variant="destructive" className="py-2">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="text-xs">
                Please set hours for at least one working day.
              </AlertDescription>
            </Alert>
          )}
        </div>

        {/* Working Days Grid - Matching General Settings with Daily Hours */}
        <div className="space-y-3">
          <Label>Working Days & Daily Hours</Label>
          <div className="p-4 bg-muted/20 rounded-lg border">
            <div className="grid grid-cols-7 gap-3">
              {days.map((day) => {
                const isWorkingDay = pattern.workingDays[day.key];
                const dailyHours = pattern.dailyHours?.[day.key] || 0;
                
                return (
                  <div key={day.key} className="space-y-2">
                    <Label className="text-xs font-medium text-center block">{day.short}</Label>
                    <div className="flex items-center justify-center">
                      <Switch
                        checked={isWorkingDay}
                        onCheckedChange={(checked) => !readOnly && handleDayToggle(day.key)}
                        disabled={readOnly}
                        className="scale-90"
                      />
                    </div>
                    <Input
                      type="number"
                      value={dailyHours.toFixed(2)}
                      onChange={(e) => !readOnly && handleDailyHoursChange(day.key, e.target.value)}
                      disabled={readOnly || !isWorkingDay}
                      className={cn(
                        "h-9 text-sm text-center",
                        isWorkingDay && !readOnly ? "bg-background" : "bg-muted/50"
                      )}
                      min="0"
                      max="24"
                      step="0.25"
                    />
                  </div>
                );
              })}
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            {readOnly 
              ? 'Daily hours breakdown for this team member\'s working pattern.'
              : 'Toggle days on/off and set specific hours for each working day. You can also edit the total weekly hours above to distribute hours evenly across working days.'
            }
          </p>
        </div>

        {/* Real-time Summary */}
        {workingDaysCount > 0 && pattern.hoursPerWeek > 0 && (
          <Alert className="bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800">
            <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
            <AlertDescription className="text-sm text-green-900 dark:text-green-100">
              <div className="space-y-1">
                <p>
                  <span className="font-medium">{workingDaysCount} working {workingDaysCount === 1 ? 'day' : 'days'}</span> per week
                </p>
                <p>
                  Average of <span className="font-medium">{hoursPerDay.toFixed(1)} hours per day</span>
                </p>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Validation Errors */}
        {workingDaysCount === 0 && !readOnly && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Please select at least one working day
            </AlertDescription>
          </Alert>
        )}

        {/* Notes */}
        {!readOnly && (
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              placeholder="e.g., Works compressed schedule, 4 x 10-hour days"
              value={pattern.notes || ''}
              onChange={(e) => handleNotesChange(e.target.value)}
              disabled={readOnly}
              rows={2}
              className="resize-none"
            />
          </div>
        )}

        {pattern.notes && readOnly && (
          <div className="space-y-2">
            <Label>Notes</Label>
            <p className="text-sm text-muted-foreground">{pattern.notes}</p>
          </div>
        )}

        {/* Legacy support: Keep individual save button if onSave is provided directly */}
        {!readOnly && onSave && (
          <div className="flex gap-2 pt-4 border-t">
            <Button 
              onClick={() => onSave(pattern)} 
              disabled={hasValidationErrors}
              className="flex-1 sm:flex-initial"
            >
              Save Changes
            </Button>
            {onCancel && (
              <Button onClick={onCancel} variant="ghost">
                Cancel
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
